<template>
  <div class="menu--section_nav menu--glass_effect menu--glass_effect-opaque"
       :class="containerClasses" @click.stop="isExpanded = false">

    <a href="#" class="menu--section_nav_handle material-icons" v-show="(menu?.sections.length || 0) > 1"
       :title="(isExpanded) ? 'Close Section Chooser' : 'Open Section Chooser'"
       v-text="(isExpanded) ? 'unfold_less' : 'unfold_more'" @click.prevent.stop="handleHandleClick"></a>

    <div class="menu--section_nav_scroller" v-if="menu">
      <ul class="menu--section_nav_list">
        <menu-section-list-item-v2 v-for="section in menu.sections" :key="'nav--' + section.ms_id"
                                   :section="section" :is-highlighted="highlightedSectionId === section.ms_id"
                                   @click.prevent.stop.native="navButtonClickHandler(section)"></menu-section-list-item-v2>
      </ul>
    </div>

    <div class="menu--section_nav_overlay" :class="{'menu--section_nav_overlay-active': isExpanded}"
         @click="isExpanded = false"></div>

  </div>
</template>

<script lang="ts">
/**
 * Section nav widget for menus.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 2.0.0
 * @copyright Copyright (C) 2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *  2025-04-04 (1.0.0) (jhoward)
 *    -
 */

import {defineComponent, PropType} from "vue";
import {Menu, MenuSection, TextualMenuComponent, VisualMenuComponent} from "./defs";
import MenuSectionListItemV2 from "./menu-section-list-item-v2.vue";

export default defineComponent({
  name: "menu-section-list",
  components: {MenuSectionListItemV2},
  props: {
    menu: {
      type: Object as PropType<Menu | null>
    },
    initialSection: {
      type: Number,
      default: -1
    }
  },
  data() {
    return {
      containerObserver: null as IntersectionObserver | null,
      sectionObserver: null as IntersectionObserver | null,
      isStickyStuck: false,
      isFixedPosition: false,
      navButtonBuffer: 16, //in pixels

      highlightedSectionId: this.initialSection,
      navButtonHighlightTimeout: 500, //in milliseconds, time it takes for window.scrollTo to smooth scroll
      navButtonHighlightTimer: -1,

      isExpanded: false,
      isExpandingOrContracting: false,
      expandingContractingTimer: -1,
      expandingContractingTimeout: 250,

      menuSwitcherHeight: '0px',
    };
  },
  mounted: function () {
    this.isFixedPosition = (window.getComputedStyle(this.$el).position === 'fixed');

    document.querySelector('html')?.addEventListener('click', () => {
      if (this.isExpanded) {
        this.isExpanded = false;
      }
    });

    this.setInitialHighlightedSectionId();
    this.setupIntersectionObservers();
  },
  computed: {
    containerClasses() {
      return {
        'menu--section_nav-is_expanded': this.isExpanded,
        'menu--section_nav-is_expanding': this.isExpandingOrContracting,
      };
    },
    // containerStyles() {
    //   return {
    //     '--menu-switcher-height': this.menuSwitcherHeight,
    //   };
    // },
  },
  watch: {
    isExpanded() {
      if (this.isExpanded) {
        document.querySelector('body')?.classList.add('disallow-scroll');
      } else {
        document.querySelector('body')?.classList.remove('disallow-scroll');
      }
    },
    menu: function () {
      this.setInitialHighlightedSectionId();
      this.setupIntersectionObservers();
    },
    // highlightedSectionId: function () {
    //   const currentSectionButton = this.$refs['menu_nav--btn_' + this.currentSection] as HTMLAnchorElement[];
    //   if (currentSectionButton && currentSectionButton.length) {
    //     const boundingClientRect = currentSectionButton[0].getBoundingClientRect();
    //     if (this.$refs?.scroller && boundingClientRect.left < this.navButtonBuffer) {
    //       (this.$refs.scroller as HTMLDivElement).scrollTo({
    //         behavior: 'smooth',
    //         left: (this.$refs.scroller as HTMLDivElement).scrollLeft + boundingClientRect.left - this.navButtonBuffer
    //       });
    //     } else if (this.$refs?.scroller && boundingClientRect.right + this.navButtonBuffer > (this.$refs.scroller as HTMLDivElement).clientWidth) {
    //       (this.$refs.scroller as HTMLDivElement).scrollTo({
    //         behavior: 'smooth',
    //         left: (this.$refs.scroller as HTMLDivElement).scrollLeft + (boundingClientRect.right + this.navButtonBuffer - (this.$refs.scroller as HTMLDivElement).clientWidth)
    //       });
    //     }
    //   }
    // },
    // currentSection: function () {
    //   this.setupNavButtonTimer();
    // }
  },
  methods: {

    // setupNavButtonTimer: function () {
    //   clearTimeout(this.navButtonHighlightTimer);
    //   this.navButtonHighlightTimer = window.setTimeout(() => {
    //     this.highlightedSectionId = this.currentSection;
    //   }, this.navButtonHighlightTimeout);
    // },

    checkStickyStuck: function (entries: IntersectionObserverEntry[]) {
      this.isStickyStuck = (entries[0].intersectionRatio < 1);
    },

    observeCurrentSection: function (entries: IntersectionObserverEntry[]) {
      for (let i = 0, l = entries.length; i < l; i++) {
        if (entries[i].isIntersecting) {
          this.highlightedSectionId = parseInt((entries[i].target as HTMLElement).dataset?.sectionId || '0', 10);
          break;
        }
      }
      // if (1 === entries.length && entries[0].intersectionRatio !== 0) {
      //   this.highlightedSectionId = parseInt((entries[0].target as HTMLElement).dataset?.sectionId || '0', 10);
      // }
    },

    // calculateRootMarginBottom: function () {
    //   return -1 * this.$el.clientHeight;
    // },
    //
    // calculateRootMarginTop: function () {
    //   const menuContainerNode = this.$el.closest('.visual_menu--container, .textual_menu--container');
    //   if (menuContainerNode) {
    //     const searchContainerNode = menuContainerNode.querySelector('.search--container');
    //     if (searchContainerNode) {
    //       return -1 * searchContainerNode.clientHeight;
    //     }
    //   }
    //   return 0;
    // },

    setupIntersectionObservers: function () {
      if (window.hasOwnProperty('IntersectionObserver')) {
        //only need to set up the observer once
        if (!this.sectionObserver) {
          this.sectionObserver = new IntersectionObserver(this.observeCurrentSection, {
            root: null,	//defaults to viewport
            rootMargin: '-400px 0px -300px 0px',//rootMarginTop + 'px 0px 0px 0px',
            threshold: [0, 1]
          });
        }

        if (this.$parent) {
          this.$parent.$el.querySelectorAll('.menu--section').forEach(el => {
            this.sectionObserver?.observe(el);
          });
        }

        if (!this.containerObserver && !this.isFixedPosition) {
          this.containerObserver = new IntersectionObserver(this.checkStickyStuck, {
            threshold: [1]
          });
          this.containerObserver.observe(this.$el);
        }
      }
    },

    navButtonClickHandler: function (section: MenuSection) {
      if (!this.isExpanded) {
        this.handleHandleClick();
      } else {
        this.isExpanded = false;
        const parentComponent: VisualMenuComponent | null = this.$parent as VisualMenuComponent | TextualMenuComponent | null;
        if (parentComponent) {
          for (let i = 0, l = parentComponent.sectionOffsets.length; i < l; i++) {
            if (parentComponent.sectionOffsets[i].id === section.ms_id) {
              parentComponent.scrollToSection(parentComponent.sectionOffsets[i]);
              this.highlightedSectionId = parentComponent.sectionOffsets[i].id;
              break;
            }
          }
        }
        window.pepTracking.event(
          this.$store.getters.displayTypeCapitalized + ' Menu',
          'Choose Section',
          section.ms_title + ' (' + section.menu_id + '/' + section.ms_id + ')'
        );
      }
    },

    handleHandleClick() {
      this.menuSwitcherHeight = (document.querySelector('.menu_switcher--container')?.clientHeight || parseInt(this.menuSwitcherHeight)) + 'px';

      this.isExpandingOrContracting = true;
      this.isExpanded = !this.isExpanded;

      window.clearTimeout(this.expandingContractingTimer);
      this.expandingContractingTimer = window.setTimeout(() => {
        this.isExpandingOrContracting = false;
      }, this.expandingContractingTimeout);

      window.pepTracking.event(
        this.$store.getters.displayTypeCapitalized + ' Menu',
        ((this.isExpanded) ? 'Expand' : 'Collapse') + ' Section Chooser'
      );
    },

    setInitialHighlightedSectionId(): void {
      if (this.highlightedSectionId === -1) {
        this.highlightedSectionId = (this.initialSection === -1 && this.menu?.sections.length)
          ? this.menu.sections[0].ms_id
          : this.initialSection;
      }
    },
  }
})
</script>

<style scoped>
.menu--section_nav {
  /*--menu-switcher-height: 0;*/
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  text-align: center;
  background-color: #FFF;
  box-shadow: 0 -4px 30px rgba(0, 0, 0, 0.1);
  z-index: 22;
}

.menu--section_nav_list {
  max-width: 500px;
  list-style: none;
  padding: 0;
  margin: 0 auto;
  display: grid;
  grid-gap: 0;
  transition: grid-gap 150ms linear;
}

.menu--section_nav-is_expanded .menu--section_nav_list {
  grid-gap: 0.5em;
}

.menu--section_nav_scroller {
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 44px;
  max-height: 100vh;
  /*max-height: calc(100vh - var(--menu-switcher-height));*/
  padding: 8px 52px;
}

.menu--section_nav:deep(.menu--section_nav_item) {
  max-height: 0;
  overflow: hidden;
}

.menu--section_nav.menu--section_nav-is_expanding:deep(.menu--section_nav_item) {
  transition: visibility 1ms linear 150ms, max-height 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.menu--section_nav.menu--section_nav-is_expanded .menu--section_nav_item,
.menu--section_nav:deep(.menu--section_nav_item-current) {
  visibility: visible;
  max-height: 6em;
}

.menu--section_nav.menu--section_nav-is_expanding.menu--section_nav-is_expanded .menu--section_nav_item,
.menu--section_nav.menu--section_nav-is_expanding:deep(.menu--section_nav_item-current) {
  transition: max-height 150ms cubic-bezier(0.4, 0, 0.2, 1);
}


.menu--section_nav_handle {
  position: absolute;
  bottom: 8px;
  right: 0;
  width: 44px;
  height: 44px;
  font-size: 44px;
  line-height: 44px;
  text-decoration: none;
}


.menu--section_nav_overlay {
  position: absolute;
  bottom: 100%;
  left: 0;
  width: 100vw;
  height: 0;
  background-color: rgba(0, 0, 0, 0.65);
  z-index: -1;
  visibility: hidden;
  opacity: 0;
  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.menu--section_nav_overlay.menu--section_nav_overlay-active {
  visibility: visible;
  height: 100vh;
  opacity: 1;
}
</style>